import Vector3 from '../math/Vector3.js';
import AOSpatialIndex from '../ao/AOSpatialIndex.js';

/**
 * CharacterCollisionSystem - Handles collision detection and response for characters
 *
 * Uses swept AABB collision detection with sliding response for smooth movement.
 * Reuses the AOSpatialIndex for efficient spatial queries against world geometry.
 */
export default class CharacterCollisionSystem {
  static EPSILON = 0.001;
  static MAX_ITERATIONS = 3; // Prevent infinite loops on corners

  /**
   * Create a new collision system
   * @param {World} world - Reference to the game world
   */
  constructor(world) {
    this.world = world;

    // Create a dedicated spatial index for collision queries
    // Uses same cell size as AOSpatialIndex for consistency
    this.spatialIndex = new AOSpatialIndex(2);

    // Whether the index needs rebuilding
    this._indexDirty = true;
  }

  /**
   * Rebuild the spatial index from world cuboids
   * Call this when the world geometry changes
   */
  rebuildIndex() {
    this.spatialIndex.rebuild(this.world.cuboids);
    this._indexDirty = false;
  }

  /**
   * Mark the index as needing rebuild
   */
  invalidate() {
    this._indexDirty = true;
  }

  /**
   * Ensure the index is up to date
   */
  ensureIndex() {
    if (this._indexDirty) {
      this.rebuildIndex();
    }
  }

  /**
   * Query cuboids that might collide with a bounding box
   * @param {Object} aabb - Axis-aligned bounding box
   * @returns {Set<Cuboid>} Set of potentially colliding cuboids
   */
  queryCuboids(aabb) {
    this.ensureIndex();
    return this.spatialIndex.queryAABB(
      aabb.minX, aabb.minY, aabb.minZ,
      aabb.maxX, aabb.maxY, aabb.maxZ
    );
  }

  /**
   * Check if two AABBs intersect
   * @param {Object} a - First AABB
   * @param {Object} b - Second AABB
   * @returns {boolean} True if intersecting
   */
  aabbIntersects(a, b) {
    return (
      a.minX < b.maxX && a.maxX > b.minX &&
      a.minY < b.maxY && a.maxY > b.minY &&
      a.minZ < b.maxZ && a.maxZ > b.minZ
    );
  }

  /**
   * Get AABB for a cuboid
   * @param {Cuboid} cuboid - The cuboid
   * @returns {Object} AABB
   */
  getCuboidAABB(cuboid) {
    return {
      minX: cuboid.x,
      minY: cuboid.y,
      minZ: cuboid.z,
      maxX: cuboid.x + cuboid.width,
      maxY: cuboid.y + (cuboid.collisionHeight || cuboid.height),
      maxZ: cuboid.z + cuboid.depth
    };
  }

  /**
   * Push a character out of any overlapping solid cuboids.
   * Called after collision is re-enabled on a cuboid to prevent getting stuck.
   * Teleports the character on top of the cuboid they're inside.
   * @param {Character} character - The character to resolve
   */
  resolveOverlaps(character) {
    if (!character) return;
    const charAABB = character.getAABBAt(character.x, character.y, character.z);
    const candidates = this.queryCuboids(charAABB);

    for (const cuboid of candidates) {
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;
      const cuboidAABB = this.getCuboidAABB(cuboid);
      if (this.aabbIntersects(charAABB, cuboidAABB)) {
        // Place character on top of the cuboid
        character.y = cuboidAABB.maxY;
        return;
      }
    }
  }

  /**
   * Check if a character's position would collide with world geometry
   * @param {Character} character - The character
   * @param {number} x - X position to test
   * @param {number} y - Y position to test
   * @param {number} z - Z position to test
   * @returns {boolean} True if collision
   */
  checkCollision(character, x, y, z) {
    const charAABB = character.getAABBAt(x, y, z);

    // Expand query box slightly for safety
    const queryBox = {
      minX: charAABB.minX - CharacterCollisionSystem.EPSILON,
      minY: charAABB.minY - CharacterCollisionSystem.EPSILON,
      minZ: charAABB.minZ - CharacterCollisionSystem.EPSILON,
      maxX: charAABB.maxX + CharacterCollisionSystem.EPSILON,
      maxY: charAABB.maxY + CharacterCollisionSystem.EPSILON,
      maxZ: charAABB.maxZ + CharacterCollisionSystem.EPSILON
    };

    const candidates = this.queryCuboids(queryBox);

    for (const cuboid of candidates) {
      // Skip non-collider cuboids (water, gas, etc.)
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cuboidAABB = this.getCuboidAABB(cuboid);
      if (this.aabbIntersects(charAABB, cuboidAABB)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Swept AABB collision test
   * @param {Object} movingBox - The moving character's AABB
   * @param {Vector3} displacement - Movement vector for this frame
   * @param {Cuboid} staticCuboid - World cuboid to test against
   * @returns {Object|null} { t: collision time 0-1, normal: collision normal }
   */
  sweepAABB(movingBox, displacement, staticCuboid) {
    const EPSILON = CharacterCollisionSystem.EPSILON;

    // Get static box AABB
    const staticBox = this.getCuboidAABB(staticCuboid);

    // Minkowski sum: expand static box by moving box dimensions
    const halfWidth = (movingBox.maxX - movingBox.minX) / 2;
    const halfHeight = (movingBox.maxY - movingBox.minY) / 2;
    const halfDepth = (movingBox.maxZ - movingBox.minZ) / 2;

    const expandedMin = {
      x: staticBox.minX - halfWidth,
      y: staticBox.minY - halfHeight,
      z: staticBox.minZ - halfDepth
    };
    const expandedMax = {
      x: staticBox.maxX + halfWidth,
      y: staticBox.maxY + halfHeight,
      z: staticBox.maxZ + halfDepth
    };

    // Ray origin is center of moving box
    const origin = {
      x: (movingBox.minX + movingBox.maxX) / 2,
      y: (movingBox.minY + movingBox.maxY) / 2,
      z: (movingBox.minZ + movingBox.maxZ) / 2
    };

    let tMin = 0;
    let tMax = 1;
    let hitNormal = null;

    const axes = ['x', 'y', 'z'];
    const normals = [
      new Vector3(-1, 0, 0), new Vector3(1, 0, 0),
      new Vector3(0, -1, 0), new Vector3(0, 1, 0),
      new Vector3(0, 0, -1), new Vector3(0, 0, 1)
    ];

    for (let i = 0; i < 3; i++) {
      const axis = axes[i];
      const d = displacement[axis];
      const min = expandedMin[axis];
      const max = expandedMax[axis];
      const o = origin[axis];

      if (Math.abs(d) < EPSILON) {
        // Parallel - check if inside slab
        if (o < min || o > max) return null;
      } else {
        let t1 = (min - o) / d;
        let t2 = (max - o) / d;
        let normal = normals[i * 2]; // Entry normal

        if (t1 > t2) {
          [t1, t2] = [t2, t1];
          normal = normals[i * 2 + 1];
        }

        if (t1 > tMin) {
          tMin = t1;
          hitNormal = normal;
        }
        tMax = Math.min(tMax, t2);

        if (tMin > tMax) return null;
      }
    }

    if (tMin >= 0 && tMin <= 1) {
      return { t: tMin, normal: hitNormal };
    }
    return null;
  }

  /**
   * Move a character with collision detection and sliding response
   * @param {Character} character - The character to move
   * @param {Vector3} velocity - Desired velocity (units per second)
   * @param {number} deltaTime - Time since last frame
   * @returns {Vector3} New position after collision response
   */
  moveWithCollision(character, velocity, deltaTime) {
    const EPSILON = CharacterCollisionSystem.EPSILON;
    const MAX_ITERATIONS = CharacterCollisionSystem.MAX_ITERATIONS;

    let remainingTime = deltaTime;
    let remainingVelocity = velocity.clone();
    let position = new Vector3(character.x, character.y, character.z);

    for (let i = 0; i < MAX_ITERATIONS && remainingTime > EPSILON; i++) {
      // Calculate displacement for remaining time
      const displacement = remainingVelocity.multiply(remainingTime);

      // Skip if negligible movement
      if (displacement.lengthSquared() < EPSILON * EPSILON) {
        break;
      }

      // Get character AABB at current position
      const charAABB = character.getAABBAt(position.x, position.y, position.z);

      // Calculate sweep volume (expand by displacement)
      const sweepBox = {
        minX: Math.min(charAABB.minX, charAABB.minX + displacement.x) - EPSILON,
        minY: Math.min(charAABB.minY, charAABB.minY + displacement.y) - EPSILON,
        minZ: Math.min(charAABB.minZ, charAABB.minZ + displacement.z) - EPSILON,
        maxX: Math.max(charAABB.maxX, charAABB.maxX + displacement.x) + EPSILON,
        maxY: Math.max(charAABB.maxY, charAABB.maxY + displacement.y) + EPSILON,
        maxZ: Math.max(charAABB.maxZ, charAABB.maxZ + displacement.z) + EPSILON
      };

      // Query nearby cuboids
      const candidates = this.queryCuboids(sweepBox);

      // Find earliest collision
      let earliestHit = { t: 1.0, normal: null };

      for (const cuboid of candidates) {
        // Skip non-collider cuboids (water, gas, etc.)
        if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

        const hit = this.sweepAABB(charAABB, displacement, cuboid);
        if (hit && hit.t < earliestHit.t) {
          earliestHit = hit;
        }
      }

      // Move to collision point (minus small epsilon to avoid penetration)
      const tSafe = Math.max(0, earliestHit.t - EPSILON);
      position = position.add(remainingVelocity.multiply(remainingTime * tSafe));

      if (earliestHit.normal) {
        // Slide: remove velocity component in normal direction
        const dot = remainingVelocity.dot(earliestHit.normal);
        remainingVelocity = remainingVelocity.subtract(earliestHit.normal.multiply(dot));
        remainingTime *= (1 - earliestHit.t);
      } else {
        break; // No collision, done
      }
    }

    return position;
  }

  /**
   * Move character with collision on each axis separately (simpler sliding)
   * @param {Character} character - The character to move
   * @param {Vector3} velocity - Desired velocity
   * @param {number} deltaTime - Time since last frame
   * @returns {Vector3} New position
   */
  moveWithAxisSeparation(character, velocity, deltaTime) {
    let newX = character.x + velocity.x * deltaTime;
    let newY = character.y + velocity.y * deltaTime;
    let newZ = character.z + velocity.z * deltaTime;

    // Try X movement
    if (!this.checkCollision(character, newX, character.y, character.z)) {
      // X movement OK
    } else {
      newX = character.x;
    }

    // Try Y movement
    if (!this.checkCollision(character, newX, newY, character.z)) {
      // Y movement OK
    } else {
      newY = character.y;
    }

    // Try Z movement
    if (!this.checkCollision(character, newX, newY, newZ)) {
      // Z movement OK
    } else {
      newZ = character.z;
    }

    // Final combined check for corners - if we moved on multiple axes,
    // verify the final position is actually valid (catches diagonal clipping)
    if (this.checkCollision(character, newX, newY, newZ)) {
      // We clipped through something! Fall back to more conservative movement.
      // Try each axis from original position
      const canMoveX = !this.checkCollision(character, character.x + velocity.x * deltaTime, character.y, character.z);
      const canMoveY = !this.checkCollision(character, character.x, character.y + velocity.y * deltaTime, character.z);
      const canMoveZ = !this.checkCollision(character, character.x, character.y, character.z + velocity.z * deltaTime);

      newX = canMoveX ? character.x + velocity.x * deltaTime : character.x;
      newY = canMoveY ? character.y + velocity.y * deltaTime : character.y;
      newZ = canMoveZ ? character.z + velocity.z * deltaTime : character.z;

      // If still colliding, don't move at all
      if (this.checkCollision(character, newX, newY, newZ)) {
        return new Vector3(character.x, character.y, character.z);
      }
    }

    return new Vector3(newX, newY, newZ);
  }

  /**
   * Find all cuboids colliding with a character at a position
   * @param {Character} character - The character
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {number} z - Z position
   * @returns {Cuboid[]} Array of colliding cuboids
   */
  findCollisions(character, x, y, z) {
    const charAABB = character.getAABBAt(x, y, z);
    const candidates = this.queryCuboids(charAABB);
    const collisions = [];

    for (const cuboid of candidates) {
      // Skip non-collider cuboids (water, gas, etc.)
      if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

      const cuboidAABB = this.getCuboidAABB(cuboid);
      if (this.aabbIntersects(charAABB, cuboidAABB)) {
        collisions.push(cuboid);
      }
    }

    return collisions;
  }

  /**
   * Move character with swept collision on Y-axis to prevent tunneling
   * Uses axis separation for X/Z but swept test for Y (falling/jumping)
   * @param {Character} character - The character to move
   * @param {Vector3} velocity - Desired velocity
   * @param {number} deltaTime - Time since last frame
   * @returns {Vector3} New position
   */
  moveWithSweptY(character, velocity, deltaTime) {
    const EPSILON = CharacterCollisionSystem.EPSILON;

    let newX = character.x;
    let newY = character.y;
    let newZ = character.z;

    // --- Handle Y movement with swept collision (prevents tunneling) ---
    if (Math.abs(velocity.y) > EPSILON) {
      const yDisplacement = velocity.y * deltaTime;
      const startY = character.y;
      const endY = character.y + yDisplacement;

      // Create sweep volume that covers the entire Y movement path
      const halfWidth = character.width / 2;
      const halfDepth = character.depth / 2;
      const sweepBox = {
        minX: character.x - halfWidth - EPSILON,
        minY: Math.min(startY, endY) - EPSILON,
        minZ: character.z - halfDepth - EPSILON,
        maxX: character.x + halfWidth + EPSILON,
        maxY: Math.max(startY, endY) + character.height + EPSILON,
        maxZ: character.z + halfDepth + EPSILON
      };

      const candidates = this.queryCuboids(sweepBox);

      if (velocity.y < 0) {
        // Falling - find highest floor we'd pass through
        let highestFloor = endY; // Default: fall to intended position

        for (const cuboid of candidates) {
          // Skip non-collider cuboids (water, gas, etc.)
          if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

          const cuboidTop = cuboid.y + cuboid.height;

          // Check horizontal overlap
          const charMinX = character.x - halfWidth;
          const charMaxX = character.x + halfWidth;
          const charMinZ = character.z - halfDepth;
          const charMaxZ = character.z + halfDepth;
          const cuboidMinX = cuboid.x;
          const cuboidMaxX = cuboid.x + cuboid.width;
          const cuboidMinZ = cuboid.z;
          const cuboidMaxZ = cuboid.z + cuboid.depth;

          const overlapsHorizontal = (
            charMinX < cuboidMaxX && charMaxX > cuboidMinX &&
            charMinZ < cuboidMaxZ && charMaxZ > cuboidMinZ
          );

          if (overlapsHorizontal) {
            // Floor is valid if:
            // - Its top is at or below where we started (startY)
            // - Its top is above where we'd end up (endY)
            // - Character's feet would pass through it
            if (cuboidTop <= startY + EPSILON && cuboidTop > endY - EPSILON) {
              highestFloor = Math.max(highestFloor, cuboidTop);
            }
          }
        }

        newY = highestFloor;
      } else {
        // Jumping/rising - find lowest ceiling we'd pass through
        let lowestCeiling = endY; // Default: rise to intended position

        for (const cuboid of candidates) {
          // Skip non-collider cuboids (water, gas, etc.)
          if (cuboid.hasNoCollision && cuboid.hasNoCollision()) continue;

          const cuboidBottom = cuboid.y;

          // Check horizontal overlap
          const charMinX = character.x - halfWidth;
          const charMaxX = character.x + halfWidth;
          const charMinZ = character.z - halfDepth;
          const charMaxZ = character.z + halfDepth;
          const cuboidMinX = cuboid.x;
          const cuboidMaxX = cuboid.x + cuboid.width;
          const cuboidMinZ = cuboid.z;
          const cuboidMaxZ = cuboid.z + cuboid.depth;

          const overlapsHorizontal = (
            charMinX < cuboidMaxX && charMaxX > cuboidMinX &&
            charMinZ < cuboidMaxZ && charMaxZ > cuboidMinZ
          );

          if (overlapsHorizontal) {
            // Ceiling is valid if:
            // - Its bottom is at or above where our head started (startY + height)
            // - Its bottom is below where our head would end up (endY + height)
            const headStart = startY + character.height;
            const headEnd = endY + character.height;

            if (cuboidBottom >= headStart - EPSILON && cuboidBottom < headEnd + EPSILON) {
              // Stop so head is just below this ceiling
              const stoppedY = cuboidBottom - character.height;
              lowestCeiling = Math.min(lowestCeiling, stoppedY);
            }
          }
        }

        newY = lowestCeiling;
      }
    }

    // --- Handle X movement (simple axis separation) ---
    const testX = character.x + velocity.x * deltaTime;
    if (!this.checkCollision(character, testX, newY, character.z)) {
      newX = testX;
    }

    // --- Handle Z movement (simple axis separation) ---
    const testZ = character.z + velocity.z * deltaTime;
    if (!this.checkCollision(character, newX, newY, testZ)) {
      newZ = testZ;
    }

    // Final safety check - if somehow still colliding, don't move
    if (this.checkCollision(character, newX, newY, newZ)) {
      // Try to at least preserve Y movement (most important for not falling through)
      if (!this.checkCollision(character, character.x, newY, character.z)) {
        return new Vector3(character.x, newY, character.z);
      }
      // Full collision - stay in place
      return new Vector3(character.x, character.y, character.z);
    }

    return new Vector3(newX, newY, newZ);
  }
}
